import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { Row, Col } from "react-bootstrap";
import { currentDateOfTheMonth } from "../../common-data/Converts";
import { fetchStaffAttendanceReportData } from "../../../redux/reducers/apiSlice";
import CmnFilters from "../../lead-management/common/CmnFilters";
import MealsStructure from "../../diet/meal/MealsStructure";
import StaffReport from "./new-ui/StaffReport";
import StaffAttendanceStats from "./stat-cards/StaffAttendanceStats";
import AttendanceExportButton from "./AttendanceExportButton";
import { fetchStaffAttendanceStatsData } from "../../../redux/prospect-reducers/prospectSlice";
import { checkIsManagerRole } from "../../common-data/Conversions";
import DeploymentMatrixTab from "./deployment-matrix/DeploymentMatrixTab";
import StaffAnalyticsTab from "./analytics/StaffAnalyticsTab";
import HeadcountTab from "./headcount/HeadcountTab";
import StaffingGoalsTab from "./staffing-goals/StaffingGoalsTab";
import NewGymsTab from "./new-gyms/NewGymsTab";
import "../../../styles/staff-report/staff-attendance-report.css";
import "../../../styles/docs/toggle-switch.css";

// NOTE: "Daily Tracker" tab intentionally NOT included — its backend endpoints
// (absent/checkout, leave/dayoff, analytics/discipline) are not present in the
// powered backend yet. The other 5 tabs reuse /staff/report/v3 (+ /gym, /staff)
// which already exist in powered, so they are read-only and need no DB change.
const TABS = {
  ATTENDANCE: "attendance",
  DEPLOYMENT_MATRIX: "deployment_matrix",
  STAFF_ANALYTICS: "staff_analytics",
  HEADCOUNT:        "headcount",
  STAFFING_GOALS:   "staffing_goals",
  NEW_GYMS:         "new_gyms",
};

const StaffAttendanceReport = ({ userInfoData, prevAllFilters }) => {
  const dispatch = useDispatch();

  const isAdmin = userInfoData?.account_type === "admin";
  const isGym = userInfoData?.account_type === "gym";
  const isManagerRole = checkIsManagerRole(userInfoData);

  const iniAllFilter = {
    selectedGym: !isAdmin && !isManagerRole
      ? {
          label: isGym ? userInfoData?.name : userInfoData?.gym_name || "",
          value:
            userInfoData?.gym_id ||
            userInfoData?.user_id ||
            userInfoData?.uid ||
            "",
        }
      : prevAllFilters
      ? prevAllFilters?.selectedGym
      : "",
    selectedCluster: "",
    selectedStaff: "",
    selectedStaffType: "",
    selectedShiftType: "",
    selectedShiftTime: "",
    selectedShiftStatus: "",
    selectedStartDate: currentDateOfTheMonth,
    selectedEndDate: currentDateOfTheMonth,
  };
  const [activeTab, setActiveTab] = useState(TABS.ATTENDANCE);
  const [allFilters, setAllFilters] = useState(iniAllFilter);
  const [includeTrainingGyms, setIncludeTrainingGyms] = useState(false);
  const iniFilters = {
    gym_id: allFilters.selectedGym?.value || "",
    cluster_manager_id: allFilters.selectedCluster?.value || "",
    user_id: allFilters.selectedStaff?.value || "",
    account_type: allFilters.selectedStaffType?.value || "",
    shift_type: allFilters.selectedShiftType?.value || "",
    slot_time: allFilters.selectedShiftTime?.value || "",
    shift_status: allFilters.selectedShiftStatus?.value || "",
    date_from: allFilters.selectedStartDate || "",
    date_to: allFilters.selectedEndDate || "",
    include_training_gyms: includeTrainingGyms ? "1" : "",
  };
  const [filters, setFilters] = useState(iniFilters);

  const onResetFilters = () => {
    setAllFilters(iniAllFilter);
    setIncludeTrainingGyms(false);
  };

  function getAllStaffAttendanceReport(filters) {
    dispatch(
      fetchStaffAttendanceReportData({
        filters: filters,
        token: userInfoData?.token,
      })
    );
  }

  function getAllStaffAttendanceStats(filters) {
    dispatch(
      fetchStaffAttendanceStatsData({
        filters: filters,
        token: userInfoData?.token,
      })
    );
  }

  useEffect(() => {
    setFilters((prevs) => ({
      ...prevs,
      gym_id: allFilters.selectedGym?.value || "",
      cluster_manager_id: allFilters.selectedCluster?.value || "",
      user_id: allFilters.selectedStaff?.value || "",
      account_type: allFilters.selectedStaffType?.value || "",
      shift_type: allFilters.selectedShiftType?.value || "",
      slot_time: allFilters.selectedShiftTime?.value || "",
      shift_status: allFilters.selectedShiftStatus?.value || "",
      date_from: allFilters.selectedStartDate || "",
      date_to: allFilters.selectedEndDate || "",
      include_training_gyms: includeTrainingGyms ? "1" : "",
    }));
  }, [
    allFilters.selectedGym,
    allFilters.selectedCluster,
    allFilters.selectedStaff,
    allFilters.selectedStaffType,
    allFilters.selectedShiftType,
    allFilters.selectedShiftTime,
    allFilters.selectedShiftStatus,
    allFilters.selectedStartDate,
    allFilters.selectedEndDate,
    includeTrainingGyms,
  ]);

  useEffect(() => {
    getAllStaffAttendanceStats(filters);
    getAllStaffAttendanceReport(filters);
  }, [filters]);

  return (
    <>
      <Row>
        <Col>
          <MealsStructure
            text={"Trainer and Sales Executives Attendance Report"}
            isReload={() => {
              getAllStaffAttendanceStats(filters);
              getAllStaffAttendanceReport(filters);
            }}
          />
        </Col>
      </Row>
      <br />

      <div className="sar-sticky-filters">
        <Row>
          <Col>
            <CmnFilters
              from={"staff-attendance-report"}
              userInfoData={userInfoData}
              onClickReset={onResetFilters}
              filters={allFilters}
              setFilters={setAllFilters}
            />
          </Col>
        </Row>

        {(isAdmin || isManagerRole) && (
          <Row>
            <Col>
              <div className="sar-training-toggle">
                <label className="docs-toggle-switch">
                  <input
                    type="checkbox"
                    className="docs-toggle-switch__input"
                    checked={includeTrainingGyms}
                    onChange={(e) => setIncludeTrainingGyms(e.target.checked)}
                  />
                  <span className="docs-toggle-switch__slider" />
                </label>
                <span className="sar-training-toggle__label">
                  Include Core Revolution (Training)
                </span>
              </div>
            </Col>
          </Row>
        )}
      </div>

      <Row>
        <Col>
          <div className="sdt-tab-header" role="tablist">
            <button
              type="button"
              role="tab"
              aria-selected={activeTab === TABS.ATTENDANCE}
              className={`sdt-tab-btn${activeTab === TABS.ATTENDANCE ? " sdt-tab-btn--active" : ""}`}
              onClick={() => setActiveTab(TABS.ATTENDANCE)}
            >
              Attendance Report
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={activeTab === TABS.DEPLOYMENT_MATRIX}
              className={`sdt-tab-btn${activeTab === TABS.DEPLOYMENT_MATRIX ? " sdt-tab-btn--active" : ""}`}
              onClick={() => setActiveTab(TABS.DEPLOYMENT_MATRIX)}
            >
              Deployment Matrix
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={activeTab === TABS.STAFF_ANALYTICS}
              className={`sdt-tab-btn${activeTab === TABS.STAFF_ANALYTICS ? " sdt-tab-btn--active" : ""}`}
              onClick={() => setActiveTab(TABS.STAFF_ANALYTICS)}
            >
              Staff Analytics
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={activeTab === TABS.HEADCOUNT}
              className={`sdt-tab-btn${activeTab === TABS.HEADCOUNT ? " sdt-tab-btn--active" : ""}`}
              onClick={() => setActiveTab(TABS.HEADCOUNT)}
            >
              Daily Headcount
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={activeTab === TABS.STAFFING_GOALS}
              className={`sdt-tab-btn${activeTab === TABS.STAFFING_GOALS ? " sdt-tab-btn--active" : ""}`}
              onClick={() => setActiveTab(TABS.STAFFING_GOALS)}
            >
              Staffing Goals
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={activeTab === TABS.NEW_GYMS}
              className={`sdt-tab-btn${activeTab === TABS.NEW_GYMS ? " sdt-tab-btn--active" : ""}`}
              onClick={() => setActiveTab(TABS.NEW_GYMS)}
            >
              New Gyms
            </button>
          </div>

          {activeTab === TABS.ATTENDANCE && (
            <>
              <StaffAttendanceStats />
              <div
                style={{
                  display: "flex",
                  justifyContent: "flex-end",
                  margin: "8px 0",
                }}
              >
                <AttendanceExportButton filters={filters} />
              </div>
              <StaffReport
                userInfoData={userInfoData}
                callFunction={() => {
                  getAllStaffAttendanceStats(filters);
                  getAllStaffAttendanceReport(filters);
                }}
              />
            </>
          )}
          {activeTab === TABS.DEPLOYMENT_MATRIX && (
            <DeploymentMatrixTab gymFilter={allFilters.selectedGym} />
          )}
          {activeTab === TABS.STAFF_ANALYTICS && (
            <StaffAnalyticsTab gymFilter={allFilters.selectedGym} />
          )}
          {activeTab === TABS.HEADCOUNT && (
            <HeadcountTab gymFilter={allFilters.selectedGym} />
          )}
          {activeTab === TABS.STAFFING_GOALS && (
            <StaffingGoalsTab gymFilter={allFilters.selectedGym} />
          )}
          {activeTab === TABS.NEW_GYMS && (
            <NewGymsTab gymFilter={allFilters.selectedGym} />
          )}
        </Col>
      </Row>
    </>
  );
};

export default StaffAttendanceReport;
